import { createRoot, createSignal } from 'solid-js';
import type {
  Ice,
  IceType,
  NodeArchetype,
  NodeRole,
  PoiKind,
  Risk,
  Target,
  Tier,
} from '~/types/game';

/** Path of the committed procgen output (Vite serves `public/` at root).
 *  Generated by tools/osm-pipeline/scripts/04-procgen.sh. */
export const NODE_DATA_URL = '/nodes/trinidad-alpha-01.geojson';

export type { NodeArchetype };

/** A fully-parsed procgen node: the map-rendering fields, the hack-modal
 *  `Target` fields, and provenance metadata, plus its coordinates. One per
 *  GeoJSON feature. */
export interface GameNode {
  id: string;
  lat: number;
  lng: number;
  /** Gameplay role — the axis the app gates on (target/shop/service). */
  role: NodeRole;
  // map-rendering fields. `kind`/`hot` are legacy procgen flavor, retained in
  // the data but no longer used for gameplay gating (superseded by `role`).
  kind: PoiKind;
  label: string;
  hot: boolean;
  // hack-modal Target fields
  name: string;
  type: string;
  addr: string;
  tier: Tier;
  firewall: string;
  iceRating: Ice;
  iceType: IceType;
  risk: Risk;
  rewardValue: number;
  crypto: string;
  reward: string;
  history: string;
  // procgen metadata / provenance
  archetype: NodeArchetype;
  rewardType: string;
  h3: string;
  osmId: number;
  osmType: string;
  world: string;
}

/** Build the hack-modal `Target` from a parsed node. The node already
 *  carries every Target field, so this is a straight projection. */
/** A node's vendor function for shop/service/dual nodes, derived from its
 *  archetype (retail → shop, social/civic → service). Dual nodes lose their
 *  pre-dual role, so the vendor kind is read from the archetype, not the role. */
export function vendorRole(n: GameNode): 'shop' | 'service' {
  return n.archetype === 'retail' ? 'shop' : 'service';
}

export function nodeToTarget(n: GameNode): Target {
  return {
    id: n.id,
    role: n.role,
    lat: n.lat,
    lng: n.lng,
    name: n.name,
    type: n.type,
    addr: n.addr,
    tier: n.tier,
    firewall: n.firewall,
    iceRating: n.iceRating,
    iceType: n.iceType,
    risk: n.risk,
    rewardValue: n.rewardValue,
    archetype: n.archetype,
    crypto: n.crypto,
    reward: n.reward,
    history: n.history,
  };
}

export type NodeLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

// Minimal structural type for the parsed feature. We validate the fields we
// rely on at runtime rather than trusting the file blindly.
interface RawFeature {
  geometry?: { type?: string; coordinates?: [number, number] };
  properties?: Record<string, unknown>;
}

function featureToNode(f: RawFeature): GameNode | null {
  const coords = f.geometry?.coordinates;
  const p = f.properties;
  if (f.geometry?.type !== 'Point' || !Array.isArray(coords) || !p) return null;
  const [lng, lat] = coords;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;
  if (typeof p.id !== 'string') return null;
  return {
    id: p.id,
    lat,
    lng,
    role: (p.role as NodeRole | undefined) ?? 'target',
    kind: p.kind as PoiKind,
    label: String(p.label ?? p.name ?? p.id),
    hot: Boolean(p.hot),
    name: String(p.name ?? ''),
    type: String(p.type ?? ''),
    addr: String(p.addr ?? ''),
    tier: p.tier as Tier,
    firewall: String(p.firewall ?? ''),
    iceRating: p.iceRating as Ice,
    iceType: (p.iceType as IceType | undefined) ?? 'WALL',
    risk: (p.risk as Risk | undefined) ?? 'low',
    rewardValue: typeof p.rewardValue === 'number' ? p.rewardValue : 0,
    crypto: String(p.crypto ?? ''),
    reward: String(p.reward ?? ''),
    history: String(p.history ?? ''),
    archetype: p.archetype as NodeArchetype,
    rewardType: String(p.reward_type ?? ''),
    h3: String(p.h3 ?? ''),
    osmId: Number(p.osm_id ?? 0),
    osmType: String(p.osm_type ?? ''),
    world: String(p.world ?? ''),
  };
}

/** Module-level node store, mirroring the geolocation / wake-lock / visibility
 *  singletons. Both MAP and the HackModal read the same parsed set. Fetches
 *  the static GeoJSON once; `load()` is re-callable for the error-state RETRY.
 *  Wrapped in createRoot so the signals have a stable owner outside any
 *  component scope. */
function createNodeStore() {
  const [nodes, setNodes] = createSignal<GameNode[]>([]);
  const [status, setStatus] = createSignal<NodeLoadStatus>('idle');
  const [error, setError] = createSignal<string | null>(null);

  const load = async (): Promise<void> => {
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch(NODE_DATA_URL, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const fc: unknown = await res.json();
      if (
        typeof fc !== 'object' ||
        fc === null ||
        (fc as { type?: unknown }).type !== 'FeatureCollection' ||
        !Array.isArray((fc as { features?: unknown }).features)
      ) {
        throw new Error('not a GeoJSON FeatureCollection');
      }
      const features = (fc as { features: RawFeature[] }).features;
      const parsed = features
        .map(featureToNode)
        .filter((n): n is GameNode => n !== null);
      setNodes(parsed);
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setNodes([]);
      setStatus('error');
    }
  };

  return { nodes, status, error, load };
}

export const nodeStore = createRoot(() => createNodeStore());
